import { useState, useEffect } from 'react'
import { Radio, X, RefreshCw, CheckCircle2, AlertCircle } from 'lucide-react'
import { useAuth } from '../../../context/AuthContext'

// Dedicated "Configure YouTube Simulcast" modal. Two modes:
//  - create: rich config → POST /live/:tbtvId/simulcast (ytProvision creates a new broadcast).
//  - edit:   applies changes to the ACTIVE broadcast → PATCH /live/:tbtvId/simulcast (no new broadcast;
//            stream/output reuse selectors are hidden since binding is fixed after creation).
// `thumbnailUrl` is the catalog's *live* image (current editor state) so the preview + push always match.
export default function SimulcastConfigModal({ item, cfLiveInputId, thumbnailUrl, mode = 'create', current = null, onClose, onProvisioned }) {
  const { api } = useAuth()
  const isEdit = mode === 'edit'

  const eventTitle = item?.title || ''
  const eventDesc = item?.short_description || ''
  const eventThumb = thumbnailUrl || item?.thumbnail_override_url || item?.thumbnail_url || null

  // Prefill: edit → from the active simulcast (`current`); create → from the saved config template.
  let tpl = {}
  try { tpl = item?.simulcast_config ? JSON.parse(item.simulcast_config) : {} } catch { tpl = {} }
  const src = isEdit && current ? {
    privacy: current.recordedPrivacy || current.youtube?.privacyStatus,
    titleOverride: current.titleOverride,
    descriptionOverride: current.descriptionOverride,
    playlistIds: current.playlistIds,
    unlistReplay: current.unlistReplay,
    pushThumbnail: current.thumbnailSynced,
  } : tpl

  const [privacy, setPrivacy] = useState(src.privacy || item?.youtube_privacy || 'unlisted')
  const [title, setTitle] = useState(src.titleOverride || eventTitle)
  const [description, setDescription] = useState(src.descriptionOverride != null ? src.descriptionOverride : eventDesc)
  const [useThumb, setUseThumb] = useState(src.pushThumbnail != null ? !!src.pushThumbnail : !!eventThumb)
  const [unlistReplay, setUnlistReplay] = useState(src.unlistReplay != null ? !!src.unlistReplay : true)

  const [playlists, setPlaylists] = useState([])
  const [selectedPlaylists, setSelectedPlaylists] = useState(Array.isArray(src.playlistIds) ? src.playlistIds : [])
  const [streams, setStreams] = useState([])
  const [outputs, setOutputs] = useState([])
  const [reuseStreamId, setReuseStreamId] = useState('') // '' = create new
  const [reuseCfOutputId, setReuseCfOutputId] = useState('') // '' = create new / auto

  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [status, setStatus] = useState('')

  useEffect(() => {
    let alive = true
    async function load() {
      setLoading(true); setLoadError('')
      try {
        const [pl, st, ot] = await Promise.all([
          api('/youtube/playlists').catch(e => ({ ok: false, message: e.message })),
          isEdit ? Promise.resolve({ ok: true, streams: [] }) : api('/youtube/streams').catch(e => ({ ok: false, message: e.message })),
          (!isEdit && cfLiveInputId) ? api(`/live-inputs/${cfLiveInputId}/outputs`).catch(() => ({ outputs: [] })) : Promise.resolve({ outputs: [] }),
        ])
        if (!alive) return
        if (pl?.ok) setPlaylists(pl.playlists || [])
        if (st?.ok && !isEdit) {
          setStreams(st.streams || [])
          if (tpl.reuseStreamId && (st.streams || []).some(s => s.id === tpl.reuseStreamId)) setReuseStreamId(tpl.reuseStreamId)
        }
        if (!isEdit) {
          const ytOutputs = (ot?.outputs || []).filter(o => /(?:^|\.)youtube\.com/i.test(o?.url || ''))
          setOutputs(ytOutputs)
          if (tpl.reuseCfOutputId && ytOutputs.some(o => (o.uid || o.id) === tpl.reuseCfOutputId)) setReuseCfOutputId(tpl.reuseCfOutputId)
        }
        if (pl && pl.ok === false) setLoadError(`Playlists: ${pl.message || 'failed to load'}`)
        else if (st && st.ok === false) setLoadError(`Streams: ${st.message || 'failed to load'}`)
      } catch (e) {
        if (alive) setLoadError(e.message)
      } finally {
        if (alive) setLoading(false)
      }
    }
    load()
    return () => { alive = false }
  }, [cfLiveInputId])

  function togglePlaylist(id) {
    setSelectedPlaylists(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id])
  }

  async function handleSubmit() {
    if (!isEdit && !cfLiveInputId) { setStatus('Assign a CF live input first.'); return }
    setSubmitting(true); setStatus('')
    try {
      let r
      if (isEdit) {
        // Edit sends full current values (the broadcast already exists; apply as-is).
        const payload = {
          privacy,
          title: title.trim() && title.trim() !== eventTitle ? title.trim() : '',
          description: description !== eventDesc ? description : '',
          pushThumbnail: !!(useThumb && eventThumb),
          thumbnailUrl: eventThumb || undefined,
          playlistIds: selectedPlaylists,
          unlistReplay: !!unlistReplay,
        }
        r = await api(`/live/${item.tbtv_id}/simulcast`, { method: 'PATCH', body: JSON.stringify(payload) })
      } else {
        const payload = {
          privacy,
          // only send overrides when they differ from the event's own values
          ...(title.trim() && title.trim() !== eventTitle ? { title: title.trim() } : {}),
          ...(description !== eventDesc ? { description } : {}),
          pushThumbnail: !!(useThumb && eventThumb),
          ...(eventThumb ? { thumbnailUrl: eventThumb } : {}),
          playlistIds: selectedPlaylists,
          unlistReplay: !!unlistReplay,
          ...(reuseStreamId ? { reuseStreamId } : {}),
          ...(reuseCfOutputId ? { reuseCfOutputId } : {}),
        }
        r = await api(`/live/${item.tbtv_id}/simulcast`, { method: 'POST', body: JSON.stringify(payload) })
      }
      if (r?.ok) {
        onProvisioned?.(r)
        onClose?.()
        return
      }
      setStatus(`Simulcast: ${r?.message || r?.error || 'failed'}`)
    } catch (e) { setStatus(`Error: ${e.message}`) }
    setSubmitting(false)
  }

  return (
    <div className="bulk-modal-overlay" onClick={onClose}>
      <div className="bulk-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 620 }}>
        <div className="bulk-modal-header">
          <Radio size={16} />
          <h3>{isEdit ? 'Edit YouTube Simulcast' : 'Configure YouTube Simulcast'}</h3>
          <button onClick={onClose} className="bulk-modal-close"><X size={16} /></button>
        </div>

        <div className="bulk-modal-body" style={{ maxHeight: '65vh', overflowY: 'auto' }}>
          {!isEdit && !cfLiveInputId && (
            <div style={{ fontSize: 13, color: 'var(--color-danger, #c0392b)', marginBottom: 10 }}>
              <AlertCircle size={13} style={{ verticalAlign: -2 }} /> Assign a CF live input to this event before configuring simulcast.
            </div>
          )}
          {isEdit && (
            <div style={{ fontSize: 12, color: 'var(--color-text-muted)', marginBottom: 10 }}>
              Editing the live broadcast in place — changes apply to the existing YouTube video (no new stream created).
            </div>
          )}

          {/* Visibility */}
          <label style={{ display: 'block', fontSize: 13, fontWeight: 600, margin: '4px 0 6px' }}>Visibility</label>
          <select value={privacy} onChange={e => setPrivacy(e.target.value)} style={{ width: '100%', marginBottom: 12 }}>
            <option value="unlisted">Unlisted</option>
            <option value="public">Public</option>
            <option value="private">Private</option>
          </select>

          {/* Title / description overrides */}
          <label style={{ display: 'block', fontSize: 13, fontWeight: 600, margin: '4px 0 6px' }}>Title <span style={{ fontWeight: 400, color: 'var(--color-text-muted)' }}>(defaults to event title)</span></label>
          <input type="text" value={title} maxLength={100} onChange={e => setTitle(e.target.value)} style={{ width: '100%', marginBottom: 12 }} />

          <label style={{ display: 'block', fontSize: 13, fontWeight: 600, margin: '4px 0 6px' }}>Description <span style={{ fontWeight: 400, color: 'var(--color-text-muted)' }}>(defaults to short description)</span></label>
          <textarea value={description} rows={4} onChange={e => setDescription(e.target.value)} style={{ width: '100%', marginBottom: 12, resize: 'vertical', flexShrink: 0, minHeight: 90, boxSizing: 'border-box' }} />

          {/* Thumbnail */}
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, marginBottom: 6 }}>
            <input type="checkbox" checked={useThumb} disabled={!eventThumb} onChange={e => setUseThumb(e.target.checked)} />
            Use event thumbnail on the YouTube video
          </label>
          {eventThumb ? (
            useThumb && <img src={eventThumb} alt="thumbnail" style={{ width: 160, borderRadius: 4, border: '1px solid var(--color-border)', marginBottom: 12 }} />
          ) : (
            <p style={{ fontSize: 11, color: 'var(--color-text-muted)', margin: '0 0 12px' }}>No catalog thumbnail set for this event.</p>
          )}

          {/* Playlists */}
          <label style={{ display: 'block', fontSize: 13, fontWeight: 600, margin: '4px 0 6px' }}>Add to playlists</label>
          {loading ? (
            <p style={{ fontSize: 12, color: 'var(--color-text-muted)' }}><RefreshCw size={12} className="spin" style={{ verticalAlign: -2 }} /> Loading…</p>
          ) : playlists.length ? (
            <div style={{ maxHeight: 160, minHeight: 72, overflowY: 'auto', border: '1px solid var(--color-border)', borderRadius: 4, padding: 8, marginBottom: 12, flexShrink: 0, boxSizing: 'border-box' }}>
              {playlists.map(p => (
                <label key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, padding: '2px 0' }}>
                  <input type="checkbox" checked={selectedPlaylists.includes(p.id)} onChange={() => togglePlaylist(p.id)} />
                  {p.title}{p.itemCount != null && <span style={{ color: 'var(--color-text-muted)' }}> ({p.itemCount})</span>}
                </label>
              ))}
            </div>
          ) : (
            <p style={{ fontSize: 11, color: 'var(--color-text-muted)', margin: '0 0 12px' }}>No playlists on the connected channel.</p>
          )}

          {/* Stream + CF-output reuse — create only (binding is fixed after the broadcast exists) */}
          {!isEdit && (
            <>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, margin: '4px 0 6px' }}>Ingest stream key</label>
              <select value={reuseStreamId} onChange={e => setReuseStreamId(e.target.value)} style={{ width: '100%', marginBottom: 12 }}>
                <option value="">Auto (adopt this input’s output, else create new)</option>
                {streams.map(s => <option key={s.id} value={s.id}>{s.title}{s.streamKey ? ` — …${String(s.streamKey).slice(-6)}` : ''}</option>)}
              </select>

              {outputs.length > 0 && (
                <>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 600, margin: '4px 0 6px' }}>Cloudflare output</label>
                  <select value={reuseCfOutputId} onChange={e => setReuseCfOutputId(e.target.value)} style={{ width: '100%', marginBottom: 12 }}>
                    <option value="">Auto (adopt existing YouTube output, else create new)</option>
                    {outputs.map(o => <option key={o.uid || o.id} value={o.uid || o.id}>{(o.url || 'output')}{o.streamKey ? ` — …${String(o.streamKey).slice(-6)}` : ''}{o.enabled === false ? ' (disabled)' : ''}</option>)}
                  </select>
                </>
              )}
            </>
          )}

          {/* Unlist replay */}
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, marginBottom: 8 }}>
            <input type="checkbox" checked={unlistReplay} onChange={e => setUnlistReplay(e.target.checked)} />
            Unlist the replay once the stream ends
          </label>

          {loadError && <p style={{ fontSize: 12, color: 'var(--color-danger, #c0392b)' }}><AlertCircle size={12} style={{ verticalAlign: -2 }} /> {loadError}</p>}
          {status && <p style={{ fontSize: 12, color: 'var(--color-danger, #c0392b)' }}>{status}</p>}
        </div>

        <div className="bulk-modal-footer">
          <button onClick={onClose} className="secondary" disabled={submitting}>Cancel</button>
          <button onClick={handleSubmit} disabled={submitting || (!isEdit && !cfLiveInputId)}>
            {submitting
              ? (isEdit ? 'Saving…' : 'Creating…')
              : (<><CheckCircle2 size={14} style={{ verticalAlign: -2 }} /> {isEdit ? 'Save Changes' : 'Create Simulcast'}</>)}
          </button>
        </div>
      </div>
    </div>
  )
}
